// The card's aggregator: for each stage, how the reader's score changed
// across it in the period's scanned games that reached it, in the five
// change bands (`ChangeBand`, `@chess/api-model`: gained by 2 pawns or
// more, slightly gained from half a pawn, held within it, slightly lost,
// lost). The opening's change is the score out of the opening
// (`openingScore`), a later stage's its `stageSwings` entry; the same
// measure the stage result cards, the swings charts and the verdict's
// deciding stage read, so a red band here is score lost in that stage, and
// the library's `stage-change` filter lists exactly those games. Registered
// with the game store by this plugin's setup.
import { aggregatorId, changeBandOf, CHANGE_BANDS, type Accumulator, type Aggregator, type ChangeBand, type GameFacts, type Phase } from '@chess/api-model';

export { CHANGE_BANDS, type ChangeBand };

export interface StageFlow {
  /** Scanned games that reached the stage with a score change. */
  games: number;
  by: Record<ChangeBand, number>;
}

/** What the card shows: one row per stage, in play order. */
export interface StageFlowResult {
  stages: Record<Phase, StageFlow>;
}

/** The band of the stage's score change in one scanned game; undefined when the game did not reach it or has no measure. */
export function bandOf(stage: Phase, g: GameFacts): ChangeBand | undefined {
  if (!g.scanned || !g.stageCurves?.[stage]) return undefined;
  const pawns = stage === 'opening' ? g.openingScore : g.stageSwings?.[stage];
  return pawns === undefined ? undefined : changeBandOf(pawns);
}

export const emptyBands = (): Record<ChangeBand, number> => ({ gained: 0, 'slightly-gained': 0, held: 0, 'slightly-lost': 0, lost: 0 });
const empty = (): StageFlow => ({ games: 0, by: emptyBands() });

export class StageFlowAggregator implements Aggregator<StageFlowResult> {
  readonly id = aggregatorId('stage-flow');

  start(): Accumulator<StageFlowResult> {
    const stages: Record<Phase, StageFlow> = { opening: empty(), middlegame: empty(), ending: empty() };
    return {
      add: (game) => {
        for (const stage of ['opening', 'middlegame', 'ending'] as const) {
          const band = bandOf(stage, game);
          if (!band) continue;
          stages[stage].games += 1;
          stages[stage].by[band] += 1;
        }
      },
      result: () => ({ stages }),
    };
  }
}
